<script setup lang="ts">
import { CssVarColors } from "@/shared/themes/CssVarColors.interface";
import type { PageBlock } from "@/views/blocks/PageBlock.interface";
import EditableElement from "@/views/page/EditableElement.vue";
import { computed } from "vue";
import type { ThreeColumnProps } from "./bento.interfaces";
import TagComponent from "./three-column/tag/TagComponent.vue";
import TitleComponent from "./three-column/title/TitleComponent.vue";
import BlockTitle from "./three-column/block/title/BlockTitle.vue";
import BlockDescription from "./three-column/block/description/BlockDescription.vue";

const props = defineProps<{
    block: PageBlock<ThreeColumnProps>;
    pageColors: CssVarColors;
}>();

const dataValues = computed(() => props.block.dataValues);
const styleValues = computed(() => props.block.styleValues);
</script>

<template>
    <div
        :id="block.webId"
        class="py-24 sm:py-32"
        :style="{ backgroundColor: styleValues.background }"
    >
        <div class="px-6">
            <EditableElement v-if="!dataValues.tag.isHidden" :blockId="block.id" fieldName="tag">
                <TagComponent :text="dataValues.tag.data.text" :textColor="styleValues.brand" />
            </EditableElement>
            <EditableElement
                v-if="!dataValues.title.isHidden"
                :blockId="block.id"
                fieldName="title"
            >
                <TitleComponent
                    :tag="dataValues.title.data.tag"
                    :text="dataValues.title.data.text"
                    :textColor="styleValues.text"
                />
            </EditableElement>
        </div>
        <div class="grid grid-cols-1 md:grid-cols-3 gap-6 mx-auto max-w-7xl mt-12 p-6 text-center">
            <div class="flex flex-col">
                <div
                    class="rounded-t-3xl rounded-b-lg md:rounded-l-3xl md:rounded-r-lg border-2 pt-8 px-8 h-full flex flex-col gap-4"
                    :style="{
                        borderColor: styleValues.border,
                        backgroundColor: styleValues.cardBackground,
                    }"
                >
                    <div>
                        <EditableElement
                            v-if="!dataValues.leftColumnTitle.isHidden"
                            :blockId="block.id"
                            fieldName="leftColumnTitle"
                        >
                            <BlockTitle
                                :text="dataValues.leftColumnTitle.data.text"
                                :textColor="styleValues.cardText"
                            />
                        </EditableElement>
                        <EditableElement
                            v-if="!dataValues.leftColumnDescription.isHidden"
                            :blockId="block.id"
                            fieldName="leftColumnDescription"
                        >
                            <BlockDescription
                                :text="dataValues.leftColumnDescription.data.text"
                                :textColor="styleValues.cardText"
                            />
                        </EditableElement>
                    </div>
                    <div class="mt-auto flex justify-center">
                        <EditableElement
                            v-if="!dataValues.leftColumnImage.isHidden"
                            :blockId="block.id"
                            fieldName="leftColumnImage"
                        >
                            <img
                                :src="dataValues.leftColumnImage.data.src"
                                :alt="dataValues.leftColumnImage.data.alt"
                                :style="{ borderColor: styleValues.border }"
                            />
                        </EditableElement>
                    </div>
                </div>
            </div>

            <div class="flex flex-col gap-6">
                <div
                    class="rounded-lg border-2 p-8 md:h-1/2 overflow-hidden flex flex-col gap-4"
                    :style="{
                        borderColor: styleValues.border,
                        backgroundColor: styleValues.cardBackground,
                    }"
                >
                    <div>
                        <EditableElement
                            v-if="!dataValues.topCenterColumnTitle.isHidden"
                            :blockId="block.id"
                            fieldName="topCenterColumnTitle"
                        >
                            <BlockTitle
                                :text="dataValues.topCenterColumnTitle.data.text"
                                :textColor="styleValues.cardText"
                            />
                        </EditableElement>
                        <EditableElement
                            v-if="!dataValues.topCenterColumnDescription.isHidden"
                            :blockId="block.id"
                            fieldName="topCenterColumnDescription"
                        >
                            <BlockDescription
                                :text="dataValues.topCenterColumnDescription.data.text"
                                :textColor="styleValues.cardText"
                            />
                        </EditableElement>
                    </div>
                    <div class="mt-auto flex justify-center">
                        <EditableElement
                            v-if="!dataValues.topCenterColumnImage.isHidden"
                            :blockId="block.id"
                            fieldName="topCenterColumnImage"
                        >
                            <img
                                :src="dataValues.topCenterColumnImage.data.src"
                                :alt="dataValues.topCenterColumnImage.data.alt"
                                class="rounded-lg"
                            />
                        </EditableElement>
                    </div>
                </div>

                <div
                    class="rounded-lg border-2 p-8 md:h-1/2 overflow-hidden flex flex-col gap-4"
                    :style="{
                        borderColor: styleValues.border,
                        backgroundColor: styleValues.cardBackground,
                    }"
                >
                    <div>
                        <EditableElement
                            v-if="!dataValues.bottomCenterColumnTitle.isHidden"
                            :blockId="block.id"
                            fieldName="bottomCenterColumnTitle"
                        >
                            <BlockTitle
                                :text="dataValues.bottomCenterColumnTitle.data.text"
                                :textColor="styleValues.cardText"
                            />
                        </EditableElement>
                        <EditableElement
                            v-if="!dataValues.bottomCenterColumnDescription.isHidden"
                            :blockId="block.id"
                            fieldName="bottomCenterColumnDescription"
                        >
                            <BlockDescription
                                :text="dataValues.bottomCenterColumnDescription.data.text"
                                :textColor="styleValues.cardText"
                            />
                        </EditableElement>
                    </div>
                    <div class="mt-auto flex justify-center">
                        <EditableElement
                            v-if="!dataValues.bottomCenterColumnImage.isHidden"
                            :blockId="block.id"
                            fieldName="bottomCenterColumnImage"
                        >
                            <img
                                :src="dataValues.bottomCenterColumnImage.data.src"
                                :alt="dataValues.bottomCenterColumnImage.data.alt"
                                class="rounded-lg"
                            />
                        </EditableElement>
                    </div>
                </div>
            </div>

            <div class="flex flex-col h-full overflow-hidden">
                <div
                    class="rounded-b-3xl rounded-t-lg md:rounded-r-3xl md:rounded-l-lg border-2 pt-8 px-8 h-full overflow-auto flex flex-col gap-4"
                    :style="{
                        borderColor: styleValues.border,
                        backgroundColor: styleValues.cardBackground,
                    }"
                >
                    <div>
                        <EditableElement
                            v-if="!dataValues.rightColumnTitle.isHidden"
                            :blockId="block.id"
                            fieldName="rightColumnTitle"
                        >
                            <BlockTitle
                                :text="dataValues.rightColumnTitle.data.text"
                                :textColor="styleValues.cardText"
                            />
                        </EditableElement>
                        <EditableElement
                            v-if="!dataValues.rightColumnDescription.isHidden"
                            :blockId="block.id"
                            fieldName="rightColumnDescription"
                        >
                            <BlockDescription
                                :text="dataValues.rightColumnDescription.data.text"
                                :textColor="styleValues.cardText"
                            />
                        </EditableElement>
                    </div>
                    <div class="mt-auto flex justify-end">
                        <EditableElement
                            v-if="!dataValues.rightColumnImage.isHidden"
                            :blockId="block.id"
                            fieldName="rightColumnImage"
                        >
                            <img
                                :src="dataValues.rightColumnImage.data.src"
                                :alt="dataValues.rightColumnImage.data.alt"
                            />
                        </EditableElement>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
